import React, { FC, useState, useEffect, useMemo, useCallback } from 'react';

import { useQuery, useMutation } from '@apollo/react-hooks';
import { Typography } from '@material-ui/core';
import { ApolloQueryResult } from 'apollo-client';
import cx from 'classnames';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useHistory, Redirect, useLocation, Link } from 'react-router-dom';

import { Loader } from '../../components/Loader';
import { CustomButton } from '../../components/UI/CustomButton';
import FullScreenBackgroundWrap from '../../components/UI/FullScreenBackgroudWrap';
import { DEFAULT_LOCALE } from '../../const';
import { AuthMode } from '../../context/AuthProvider/AuthContext';
import { GET_INVITE_INFO_BY_HASH, JOIN_TEAM_BY_HASH } from '../../graphql/user';
import { HOME_ROUTE, JOIN_TEAM_LINK, ROOT_ROUTE } from '../../routes';
import {
  LearnerInfo,
  Tenant,
  Query,
  GetTeamInviteInfoArgs,
  Mutation,
  JoinTeamByInviteHashArgs,
} from '../../types';
import { useAuth, useEventListener } from '../../utils/hooks';
import useStyles from './styles';

const TEXT_CHANGE_EVENT = 'invite-text-changed';

const getText = (key: string, locale: string) => {
  const textObj = window?.invitePageCustomTexting?.[locale]
    ? window.invitePageCustomTexting[locale]
    : window.invitePageCustomTexting?.[DEFAULT_LOCALE];

  return textObj && textObj[key] ? textObj[key] : null;
};

interface InvitePageProps {
  tenant?: Tenant | null;
  userInfo?: LearnerInfo;
  userLoading: boolean;
  refetchUserInfo: (
    variables?: Record<string, any> | undefined,
  ) => Promise<ApolloQueryResult<Pick<Query, 'getLearnerInfo'>>>;
  setIsAfterAuthorization: React.Dispatch<React.SetStateAction<boolean>>;
}

const InvitePage: FC<InvitePageProps> = ({
  tenant,
  userInfo,
  userLoading,
  refetchUserInfo,
  setIsAfterAuthorization,
}) => {
  const { t, i18n } = useTranslation();

  const [title, setTitle] = useState(getText('title', i18n.language));
  const [description, setDescription] = useState(getText('description', i18n.language));
  const [signUpBtnText, setSignUpBtnText] = useState(getText('signUpBtn', i18n.language));

  const classes = useStyles();

  const params = useParams();
  const hash = params?.hash;

  const history = useHistory();
  const location = useLocation();

  const [loadingToken, setLoadingToken] = useState(false);
  const { getAccessTokenSilently, loginWithRedirect } = useAuth();

  const visitedLink = useMemo(() => {
    return location?.search?.slice(1) === 'visited';
  }, [location]);

  const { data: inviteData, loading: loadingInviteData } = useQuery<
    Pick<Query, 'getInviteInfoByHash'>,
    GetTeamInviteInfoArgs
  >(GET_INVITE_INFO_BY_HASH, {
    variables: {
      hash: hash || '',
    },
    skip: !hash,
  });

  const inviteInfo = inviteData?.getInviteInfoByHash;
  const hasAvailableSeats = inviteInfo?.seats_available && inviteInfo?.seats_available > 0;

  const [joinTeam, { loading: loadingInvitation }] = useMutation<
    Pick<Mutation, 'joinTeamByInviteHash'>,
    JoinTeamByInviteHashArgs
  >(JOIN_TEAM_BY_HASH, {
    variables: {
      hash: hash,
    },
    onCompleted: async () => {
      setLoadingToken(true);
      try {
        await getAccessTokenSilently({ ignoreCache: true });
        await refetchUserInfo();
        setIsAfterAuthorization(true);
        history.replace('/home');
      } catch (e) {
        try {
          loginWithRedirect({
            redirectUri: '/my-account',
          });
        } catch (e) {}
      }
    },
  });

  const handleTextChangeEvent = useCallback(() => {
    setTitle(getText('title', i18n.language));
    setDescription(getText('description', i18n.language));
    setSignUpBtnText(getText('signUpBtn', i18n.language));
  }, [i18n]);

  //used to update text if custom was provided via gtm
  useEventListener(TEXT_CHANGE_EVENT, handleTextChangeEvent);

  const login = (mode?: AuthMode) => {
    loginWithRedirect({
      redirectUri: `${JOIN_TEAM_LINK}${hash}?visited`,
      mode: mode,
    });
  };

  useEffect(() => {
    if (userInfo && userInfo?.teams?.length === 0 && hasAvailableSeats && visitedLink) {
      joinTeam();
    }
  }, [userInfo, joinTeam, inviteInfo, visitedLink, hasAvailableSeats]);

  if (
    userLoading ||
    loadingInviteData ||
    loadingInvitation ||
    loadingToken ||
    (userInfo && hasAvailableSeats && visitedLink)
  ) {
    return (
      <>
        <Helmet>
          <title>Invitation {tenant?.title ? `- ${tenant.title}` : ''}</title>
        </Helmet>
        <Loader />
      </>
    );
  }

  if (!loadingInviteData && !inviteInfo) {
    return <Redirect to="/404" />;
  }

  const sameCompany = userInfo?.company?.id === inviteInfo?.company?.id;

  return (
    <>
      <Helmet>
        <title>Invitation {tenant?.title ? `- ${tenant.title}` : ''}</title>
      </Helmet>
      <FullScreenBackgroundWrap logoUrl={tenant?.logo_url}>
        <div className={classes.wrapper}>
          <Typography variant="h2" className={classes.title}>
            {title || `${t('page.invite.join')} ${inviteInfo?.company?.name}`}
          </Typography>
          {Boolean(!userInfo && hasAvailableSeats) && (
            <Typography variant="h3" className={classes.description}>
              {description ||
                t('page.invite.joinText', {
                  teamName: inviteInfo?.team_name,
                  organizationName: inviteInfo?.company?.name,
                  tenantTitle: tenant?.title,
                })}
            </Typography>
          )}
          {Boolean(userInfo?.teams?.length === 0 && hasAvailableSeats) && (
            <Typography variant="h3" className={classes.description}>
              {t('page.invite.joinAuthorizedText', {
                teamName: inviteInfo?.team_name,
                organizationName: inviteInfo?.company?.name,
              })}
            </Typography>
          )}
          {Boolean(userInfo?.teams && userInfo?.teams?.length > 0 && hasAvailableSeats) && (
            <>
              <Typography variant="h3" className={classes.description}>
                {t('page.invite.changeTeam', {
                  teamName: inviteInfo?.team_name,
                  organizationName: inviteInfo?.company?.name,
                })}
              </Typography>
              {!sameCompany && (
                <Typography variant="h3" className={classes.description}>
                  {t('page.invite.changeTeamSecond', {
                    userTeam: userInfo?.teams && userInfo?.teams[0]?.name,
                    userCompany: userInfo?.company?.name,
                  })}
                </Typography>
              )}
            </>
          )}
          {!hasAvailableSeats && (
            <>
              <Typography variant="h3" className={classes.description}>
                {t('page.invite.changeTeam', {
                  teamName: inviteInfo?.team_name,
                  organizationName: inviteInfo?.company?.name,
                })}
              </Typography>
              <Typography variant="h3" className={classes.description}>
                {t('page.invite.noAvailableSeats')}
              </Typography>
            </>
          )}
          {Boolean(hasAvailableSeats && !userInfo) && (
            <div className={classes.buttonsWrapper}>
              <CustomButton onClick={() => login('signUp')}>
                {signUpBtnText || t('button.signUp')}
              </CustomButton>
              <CustomButton btnClass={classes.btn} onClick={() => login()}>
                {t('button.iHaveAccount')}
              </CustomButton>
            </div>
          )}
          {Boolean(hasAvailableSeats && userInfo) && (
            <div className={cx(classes.buttonsWrapper, classes.buttonsWrapperCentered)}>
              <Link to="/home">
                <CustomButton color="secondary">{t('button.goBack')}</CustomButton>
              </Link>
              <CustomButton onClick={() => joinTeam()}>{t('button.joinTeam')}</CustomButton>
            </div>
          )}
          {!hasAvailableSeats && (
            <Link to={userInfo ? HOME_ROUTE : ROOT_ROUTE}>
              <CustomButton color="primary" btnClass={classes.joinBtn}>
                {t('button.goBack')}
              </CustomButton>
            </Link>
          )}
        </div>
      </FullScreenBackgroundWrap>
    </>
  );
};

export default InvitePage;
